/**
 * Central app state (Svelte 5 runes). Single source of truth the player and
 * widgets read from. Playback time drives a derived sample view; overlay
 * widgets are freely placed (with snapping) and added from a categorized
 * palette. Layout, dock size, units, map mode all persist.
 */

import type { Component } from 'svelte';
import { loadFlightFromSrt } from './flight.ts';
import { indexAtTime, viewAt, type Flight, type SampleView } from './model/types.ts';
import { detectEvents, type FlightEvent } from './events.ts';
import type { UnitSystem } from './units/units.ts';
import type { PathColorBy } from './pathMetric.ts';
import { type StageLayout, type SourceId, type TileNode, type DockHint, leaf, sources as treeSources, addSource, removeSource, hasSource, pruneTo, fromModes, dockHint, dockOnto } from './stage/tiling.ts';

export type { PathColorBy };
export type { StageLayout, SourceId } from './stage/tiling.ts';

/** A viz opened fullscreen in a modal. */
export interface ModalViz {
  title: string;
  comp: Component<Record<string, unknown>>;
  props: Record<string, unknown>;
}

/** Configurable map-overlay options (persisted). Everything the user can turn
 *  on/off or tune from the map's options popover lives here so the flight-path
 *  overlay is fully configurable, not hard-wired. */
export interface MapOpts {
  /** Extrude OSM buildings in 3D (OpenFreeMap vector source). */
  buildings: boolean;
  /** Event pins on the track. */
  showEvents: boolean;
  /** "GPS lost here" chips at each outage gap. */
  showLoss: boolean;
  /** Cross-highlight cursor shown while hovering the timeline/charts. */
  showHover: boolean;
  /** Track line width (px). */
  pathWidth: number;
  /** White casing under the track so it reads over busy satellite imagery. */
  pathGlow: boolean;
  /** 3D altitude "curtain" dropped from the path to the ground. */
  curtain3d: boolean;
  /** Camera line-of-sight cone (approx: flight heading, no gimbal data). */
  showLos: boolean;
  /** Project the current video frame onto the map at the (approx) footprint. */
  videoProjection: boolean;
  /** Opacity of the video-on-map projection (0.1–1). */
  videoProjOpacity: number;
}

/** Imperative map actions the FlightMap pane registers while mounted, so the
 *  central Map-settings menu in the control bar can drive the live map. Null
 *  when no map pane is open. Not persisted (functions). */
export interface MapApi {
  recenter: () => void;
  resetView: () => void;
  /** Force a synchronous map render (offline export steps frame-by-frame). */
  redraw: () => void;
  /** Re-centre on the drone synchronously if auto-follow is on — the live effect
   *  throttles on wall-clock + animates, neither of which works for offline steps. */
  syncCamera: () => void;
  /** The live MapLibre canvas, for compositing the map into the export frame. */
  canvas: () => HTMLCanvasElement | null;
}

export const DEFAULT_MAP_OPTS: MapOpts = {
  buildings: false,
  showEvents: true,
  showLoss: true,
  showHover: true,
  pathWidth: 3.5,
  pathGlow: false,
  curtain3d: true,
  showLos: true,
  videoProjection: false,
  videoProjOpacity: 0.65,
};

/** Manual colour grade applied to the video (composites over the auto-LUT and
 *  burns into exports). All sliders are 0-centred, range −100..100; the grade is
 *  inactive when every value is 0. brightness/contrast/saturation/exposure map to
 *  CSS filters; temperature/highlights/shadows to an SVG filter. */
export interface ColorAdjust {
  brightness: number;
  contrast: number;
  saturation: number;
  exposure: number;
  temperature: number;
  highlights: number;
  shadows: number;
}
export const DEFAULT_COLOR_ADJUST: ColorAdjust = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  exposure: 0,
  temperature: 0,
  highlights: 0,
  shadows: 0,
};

/** How the stage shows video vs map. (3D is now a property of the map itself.) */
export type MainView = 'video' | 'map' | 'split';

/** Colours used to distinguish overlaid comparison flights on the map. */
export const COMPARE_COLORS = ['#f59e0b', '#a78bfa', '#f472b6', '#60a5fa', '#34d399', '#fb7185'];

/** Only the path channels an overlay actually draws — keeping the whole Flight
 *  (all ~15 channels × thousands of samples) alive just to render a line was pure
 *  retention. Holding these 4 typed arrays lets the rest of the flight be GC'd. */
export interface ComparePath {
  lat?: Float64Array;
  lon?: Float64Array;
  enuE?: Float64Array;
  enuN?: Float64Array;
  length: number;
}
export interface CompareFlight {
  id: string;
  /** Library entry key of the source flight (unique even when names collide). */
  srcKey?: string;
  name: string;
  path: ComparePath;
  color: string;
}

/** Catalog ids are open strings (time-series are generated per channel). */
export type WidgetType = string;

export type WidgetCategory = 'Flight' | 'Position' | 'Camera' | 'Charts' | 'Distribution' | 'Relation' | '3D';
export const WIDGET_CATEGORIES: WidgetCategory[] = ['Flight', 'Position', 'Camera', 'Charts', 'Distribution', 'Relation', '3D'];

/** Per-widget background style: framed dark card (default), lighter glass, or
 *  frameless (just the instrument, for clean HUD overlays over the video/map). */
export type WidgetBg = 'card' | 'glass' | 'none';

export interface WidgetConfig {
  id: string;
  type: WidgetType;
  label: string;
  enabled: boolean;
  /** Legacy fractional top-left position (0..1). Kept as the seed for the first
   *  layout; once anchored (ax/ay set) the edge offsets below are authoritative. */
  x: number;
  y: number;
  /** Edge anchoring: which horizontal/vertical edge the widget is pinned to, and
   *  the px offset from that edge to the widget's matching edge. Computed from
   *  x/y on first layout, then updated on drag — so widgets stay pinned to their
   *  edge/corner when the stage resizes instead of drifting proportionally. */
  ax?: 'l' | 'r';
  ay?: 't' | 'b';
  ox?: number;
  oy?: number;
  /** Uniform display scale (0.6–2.2) — used by 'aspect' (instrument) widgets. */
  scale: number;
  /** Explicit box size (px) — used by 'free' (data-view) widgets, resized in
   *  each axis independently. Absent → falls back to the type's default box. */
  width?: number;
  height?: number;
  /** Optional accent override (CSS color). */
  color?: string;
  /** Background style: 'card' (default) | 'glass' | 'none' (frameless). */
  bg?: WidgetBg;
  /** Widget opacity 0.2–1 (for HUD overlays over video/map). Default 1. */
  opacity?: number;
  /** Hide the widget's own title row (leaner HUD look). Default false. */
  titleHidden?: boolean;
  /** Docked into the tiling stage as a pane (source id `w:<type>`) instead of a
   *  floating HUD overlay. The overlay form IS the widget's PiP; docking gives it
   *  a grid slot. Default false (overlay). */
  docked?: boolean;
  /** Per-widget-type visualization options (chart smoothing, gauge max, …).
   *  Open-shaped so each widget reads the keys it understands. */
  opts?: Record<string, number | string | boolean>;
}

export interface WidgetMeta {
  type: WidgetType;
  label: string;
  icon: string;
  category: WidgetCategory;
  /** One-line "what it shows", surfaced as a catalog tooltip. */
  desc?: string;
  /** Channel keys this widget needs; it auto-hides if any are absent. */
  requires: string[];
  x: number;
  y: number;
  on: boolean;
  /** Fixed width (px) when placed as an overlay (charts need this). */
  w?: number;
  /**
   * True for pure-readout widgets with no in-body pointer interaction — their
   * whole body can be grabbed to move them. Interactive widgets (charts that
   * hover/scrub, clickable lists, 3D orbit) omit this and drag only from the
   * title bar, so body pointer events reach the widget.
   */
  bodyDrag?: boolean;
  /** Chart binding (for generic chart widgets). */
  channel?: string;
  channel2?: string;
  color?: string;
  labelX?: string;
  labelY?: string;
}

// Time-series channels → one chart widget each.
const TS: [string, string, string, string][] = [
  ['rel_alt', 'Altitude', '⛰️', 'var(--accent)'],
  ['ground_speed', 'Ground speed', '💨', 'var(--warn)'],
  ['vertical_speed', 'Vertical speed', '↕️', 'var(--good)'],
  ['acceleration', 'Acceleration', '⚡', '#f472b6'],
  ['turn_rate', 'Turn rate', '🔄', '#22d3ee'],
  ['climb_angle', 'Climb angle', '📐', '#a3e635'],
  ['dist_from_home', 'Distance', '📍', 'var(--text-soft)'],
  ['path_length', 'Path length', '🧵', '#a78bfa'],
  ['ev', 'Exposure (EV)', '🔆', '#f59e0b'],
  ['color_temp', 'Colour temp', '🌡️', '#60a5fa'],
  ['frame_interval', 'Frame interval', '🎞️', '#94a3b8'],
];

/** Unified catalog — drives both the dock and the overlays. */
export const WIDGET_REGISTRY: WidgetMeta[] = [
  // Flight HUD
  { type: 'speed', label: 'Speed gauge', icon: '🚀', category: 'Flight', requires: ['ground_speed'], x: 0.012, y: 0.74, on: true, desc: 'Current ground speed on an arc gauge, with the trip max marked.', bodyDrag: true },
  { type: 'vspeed', label: 'Vertical speed', icon: '↕️', category: 'Flight', requires: ['vertical_speed'], x: 0.14, y: 0.78, on: true, desc: 'Climb (+) or descent (−) rate right now.', bodyDrag: true },
  { type: 'wind', label: 'Wind', icon: '🌬️', category: 'Flight', requires: ['ground_speed', 'course'], x: 0.85, y: 0.74, on: true, desc: 'Estimated wind speed and direction, from how the drone holds position.', bodyDrag: true },
  { type: 'altitude', label: 'Altitude tape', icon: '📏', category: 'Flight', requires: ['rel_alt'], x: 0.93, y: 0.3, on: false, desc: 'Altitude on a vertical tape, like a cockpit altimeter.', bodyDrag: true },
  { type: 'sticks', label: 'Stick input', icon: '🕹️', category: 'Flight', requires: ['vertical_speed', 'turn_rate'], x: 0.5, y: 0.78, on: false, desc: 'Estimated Mode-2 control inputs from the drone’s motion (uses real aircraft attitude when a flight log is loaded).', bodyDrag: true },
  { type: 'gimbal', label: 'Gimbal', icon: '🎥', category: 'Camera', requires: ['gimbal_pitch'], x: 0.85, y: 0.5, on: false, desc: 'Camera tilt (pitch) + roll from the DJI flight log.', bodyDrag: true },
  // Position
  { type: 'telemetry', label: 'Telemetry', icon: '🔢', category: 'Position', requires: ['rel_alt'], x: 0.012, y: 0.04, on: true, desc: 'Key live numbers: altitude, distance, path length, coordinates.', bodyDrag: true },
  { type: 'highlights', label: 'Highlights', icon: '⭐', category: 'Flight', requires: ['rel_alt'], x: 0.012, y: 0.3, on: false, w: 260, desc: 'Peak moments (max altitude, top speed, farthest…) with values — click to jump.' },
  { type: 'compass', label: 'Compass', icon: '🧭', category: 'Position', requires: ['course'], x: 0.45, y: 0.03, on: true, desc: 'Heading (direction of travel) on a compass rose.', bodyDrag: true },
  { type: 'path', label: 'Flight path', icon: '🗺️', category: 'Position', requires: ['enu_e', 'enu_n'], x: 0.8, y: 0.52, on: false, desc: 'Top-down shape of the flight, with the current position marked.' },
  { type: 'radar', label: 'Distance radar', icon: '🎯', category: 'Position', requires: ['dist_from_home', 'enu_e'], x: 0.8, y: 0.06, on: false, w: 170, desc: 'Distance and bearing from the takeoff point, radar-style.' },
  // Camera
  { type: 'camera', label: 'Camera', icon: '📷', category: 'Camera', requires: ['iso'], x: 0.78, y: 0.04, on: true, desc: 'Live camera settings: ISO, shutter, aperture, EV, white balance, focal length.', bodyDrag: true },
  // Charts (time-series, generated)
  ...TS.map(
    ([ch, label, icon, color], i): WidgetMeta => ({
      type: `ts_${ch}`,
      label: `${label} chart`,
      icon,
      category: 'Charts',
      requires: [ch],
      x: 0.28 + (i % 3) * 0.02,
      y: 0.08 + (i % 4) * 0.04,
      on: false,
      w: 300,
      channel: ch,
      color,
      desc: `${label} across the whole flight — hover to read, drag to scrub the timeline.`,
    }),
  ),
  // Distributions
  { type: 'hist_ground_speed', label: 'Speed histogram', icon: '📊', category: 'Distribution', requires: ['ground_speed'], x: 0.3, y: 0.6, on: false, w: 280, channel: 'ground_speed', color: 'var(--warn)', desc: 'How much of the flight was spent at each speed.' },
  { type: 'hist_rel_alt', label: 'Altitude histogram', icon: '📊', category: 'Distribution', requires: ['rel_alt'], x: 0.32, y: 0.62, on: false, w: 280, channel: 'rel_alt', color: 'var(--accent)', desc: 'How much time was spent at each altitude.' },
  { type: 'color_hist', label: 'Colour histogram', icon: '🎨', category: 'Distribution', requires: [], x: 0.34, y: 0.64, on: false, w: 300, desc: 'RGB colour distribution of the current video frame.' },
  { type: 'heading_rose', label: 'Heading rose', icon: '🧭', category: 'Charts', requires: ['course'], x: 0.6, y: 0.5, on: false, w: 220, desc: 'Which compass directions the drone flew toward most.' },
  // Relations
  { type: 'sc_alt_speed', label: 'Altitude vs Speed', icon: '✳️', category: 'Relation', requires: ['ground_speed', 'rel_alt'], x: 0.3, y: 0.1, on: false, w: 300, channel: 'ground_speed', channel2: 'rel_alt', labelX: 'Speed', labelY: 'Altitude', color: 'var(--accent)', desc: 'How altitude and speed relate — each dot is one moment.' },
  { type: 'sc_alt_dist', label: 'Altitude vs Distance', icon: '✳️', category: 'Relation', requires: ['dist_from_home', 'rel_alt'], x: 0.32, y: 0.12, on: false, w: 300, channel: 'dist_from_home', channel2: 'rel_alt', labelX: 'Distance', labelY: 'Altitude', color: 'var(--warn)', desc: 'Altitude against distance from home — the flight’s vertical profile outward.' },
  // 3D
  { type: 'view3d', label: '3D flight path', icon: '🧊', category: '3D', requires: ['enu_e', 'rel_alt'], x: 0.6, y: 0.1, on: false, w: 300, desc: 'The path in 3D at real altitude — drag to orbit.' },
];

export const WIDGET_BY_TYPE: Record<string, WidgetMeta> = Object.fromEntries(WIDGET_REGISTRY.map((m) => [m.type, m]));

// Resize family. 'aspect' instruments (round dials, maps) resize uniformly so
// they never distort; 'free' data views (charts, tables, tapes, lists) resize
// in width and height independently and reflow to fill the space.
const ASPECT_WIDGETS = new Set(['speed', 'vspeed', 'wind', 'compass', 'radar', 'path', 'heading_rose', 'sticks', 'gimbal']);
export function widgetResize(type: string): 'aspect' | 'free' {
  return ASPECT_WIDGETS.has(type) ? 'aspect' : 'free';
}
// Default box (px) for a free widget when it has no saved size yet.
export function widgetFreeSize(type: string): { w: number; h: number } {
  if (type.startsWith('ts_') || type.startsWith('hist_') || type.startsWith('sc_') || type === 'color_hist') return { w: 300, h: 150 };
  if (type === 'altitude') return { w: 120, h: 220 };
  if (type === 'telemetry' || type === 'highlights') return { w: 230, h: 200 };
  if (type === 'view3d') return { w: 340, h: 240 };
  return { w: 220, h: 150 }; // camera + fallback
}

// ---- Layout presets --------------------------------------------------------
export interface PresetPlacement {
  x: number;
  y: number;
  scale: number;
  color?: string;
}
export interface LayoutPreset {
  id: string;
  name: string;
  builtin: boolean;
  /** Enabled widgets and their geometry; absent widgets are hidden. */
  layout: Record<string, PresetPlacement>;
}

// Purpose-oriented built-in presets. 'overview' keeps the tuned HUD positions;
// the analysis presets grid-place their widgets so they don't overlap.
type PresetDef = {
  id: string;
  name: string;
  types: string[];
  grid?: boolean;
  /** Explicit HUD placements (fraction of the stage) for hand-tuned presets. */
  places?: Record<string, { x: number; y: number; scale?: number }>;
};
const PRESET_DEFS: PresetDef[] = [
  { id: 'overview', name: '🛩 Flight overview', types: ['speed', 'vspeed', 'wind', 'telemetry', 'compass', 'camera'] },
  // Mimics the DJI Fly / RC2 in-flight commander HUD: the H/D/H·S/V·S telemetry
  // readout up top, speed + vertical-speed on the left, heading + gimbal on the
  // right, and the twin control sticks along the bottom.
  {
    id: 'rc2',
    name: '🎮 RC2 commander',
    types: ['telemetry', 'speed', 'vspeed', 'compass', 'gimbal', 'sticks'],
    places: {
      telemetry: { x: 0.36, y: 0.03 },
      speed: { x: 0.02, y: 0.28 },
      vspeed: { x: 0.02, y: 0.6 },
      compass: { x: 0.84, y: 0.05 },
      gimbal: { x: 0.85, y: 0.42 },
      sticks: { x: 0.36, y: 0.66 },
    },
  },
  { id: 'speedalt', name: '💨 Speed & altitude', types: ['speed', 'altitude', 'ts_ground_speed', 'ts_rel_alt', 'ts_vertical_speed', 'sc_alt_speed'], grid: true },
  { id: 'position', name: '🧭 Position & nav', types: ['telemetry', 'compass', 'path', 'radar', 'heading_rose', 'highlights'], grid: true },
  { id: 'camera', name: '📷 Camera & exposure', types: ['camera', 'ts_ev', 'ts_color_temp', 'color_hist'], grid: true },
  { id: 'empty', name: '🚫 Clean (no widgets)', types: [] },
];
function buildPresetLayout(def: PresetDef): Record<string, PresetPlacement> {
  const out: Record<string, PresetPlacement> = {};
  def.types.forEach((t, i) => {
    const m = WIDGET_BY_TYPE[t];
    if (!m) return;
    const p = def.places?.[t];
    out[t] = p
      ? { x: p.x, y: p.y, scale: p.scale ?? 1 }
      : def.grid
        ? { x: 0.02 + (i % 3) * 0.33, y: 0.05 + Math.floor(i / 3) * 0.32, scale: 1 }
        : { x: m.x, y: m.y, scale: 1 };
  });
  return out;
}
export const BUILTIN_PRESETS: LayoutPreset[] = PRESET_DEFS.map((d) => ({ id: d.id, name: d.name, builtin: true, layout: buildPresetLayout(d) }));

function layoutsEqual(a: Record<string, PresetPlacement>, b: Record<string, PresetPlacement>): boolean {
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    const pa = a[k], pb = b[k];
    if (!pb) return false;
    const r = (v: number) => Math.round(v * 1000);
    if (r(pa.x) !== r(pb.x) || r(pa.y) !== r(pb.y) || r(pa.scale) !== r(pb.scale) || (pa.color ?? '') !== (pb.color ?? '')) return false;
  }
  return true;
}

const LS_KEY = 'drone-player-layout-v4';

interface SavedWidget {
  enabled: boolean;
  x: number;
  y: number;
  ax?: 'l' | 'r';
  ay?: 't' | 'b';
  ox?: number;
  oy?: number;
  scale: number;
  width?: number;
  height?: number;
  color?: string;
  bg?: WidgetBg;
  docked?: boolean;
}
interface SavedLayout {
  unitSystem: UnitSystem;
  dockFrac: number;
  dockCollapsed: boolean;
  lutEnabled: boolean;
  colorAdjust?: Partial<ColorAdjust>;
  timelineCollapsed?: boolean;
  timelineHeight?: number;
  endAlignPrefixes?: string[];
  mainView: MainView;
  splitFrac: number;
  splitVideoLeft: boolean;
  pipX: number;
  pipY: number;
  pipW: number;
  pathColorBy: PathColorBy;
  autoFollow: boolean;
  timelineEvents: boolean;
  map3d: boolean;
  basemap: 'streets' | 'satellite';
  mapOpts: MapOpts;
  stage?: StageLayout;
  stageCustomized?: boolean;
  flightsMigrated?: boolean;
  widgets: Record<string, SavedWidget>;
  userPresets: LayoutPreset[];
  activePresetId: string;
  lastLayout: LayoutPreset | null;
}

function defaultWidgets(): WidgetConfig[] {
  return WIDGET_REGISTRY.map((m, i) => {
    const free = widgetResize(m.type) === 'free';
    const s = free ? widgetFreeSize(m.type) : null;
    return {
      id: `${m.type}-${i}`,
      type: m.type,
      label: m.label,
      enabled: m.on,
      x: m.x,
      y: m.y,
      scale: 1,
      width: s?.w,
      height: s?.h,
    };
  });
}

// M6: a persisted layout is untrusted input — a stale/corrupt/older-schema blob could have
// any field missing or malformed. Reject non-objects here, and TYPE it as Partial so every
// downstream read must guard the field (the constructor already does), instead of the old
// `as SavedLayout` that lied about the shape and let an unguarded field through.
function loadLayout(): Partial<SavedLayout> | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Partial<SavedLayout>;
  } catch {
    return null;
  }
}

/** One video file placed on the master timeline at `offsetMs` (multi-video). */
export interface VideoClip {
  id: string;
  url: string;
  name: string;
  offsetMs: number;
}
/** A track = one on-screen surface playing an ordered sequence of clips. */
export interface VideoTrack {
  id: string;
  name: string;
  clips: VideoClip[];
  /** The graded "hero" video (D-Log M drone footage): receives the LUT, manual colour
   *  grade and zoom/pan, and is the graded segment on export. At most one track has this.
   *  This is the fold of the former special `videoUrl` primary into the track model. */
  graded?: boolean;
}

class AppState {
  flight = $state<Flight | null>(null);
  // Video TRACKS synced to the master clock (multi-video timeline). One is the graded
  // "hero" (former primary), flagged `graded` — it renders in the 'video' pane and takes
  // the LUT/zoom. A track is one surface playing an ordered SEQUENCE of clips (DJI_0001 →
  // 0002), each placed on the master timeline by its own `offsetMs`.
  // Transient object URLs — not persisted (re-drop to re-link, like the flight).
  videoTracks = $state<VideoTrack[]>([]);
  private trackSeq = 0;
  private clipSeq = 0;
  /** The graded "hero" track's id (the folded former primary video), or null. */
  gradedTrackId = $derived.by<string | null>(() => this.videoTracks.find((t) => t.graded)?.id ?? null);
  /** Back-compat aliases: the graded hero's url/name. The primary is now a graded track,
   *  but readers (LUT, export, preview, histogram, layout) still ask via these. */
  get videoUrl(): string | null {
    return this.videoTracks.find((t) => t.graded)?.clips[0]?.url ?? null;
  }
  get videoName(): string {
    return this.videoTracks.find((t) => t.graded)?.name ?? '';
  }
  /** Audio MIXER: every video plays its own audio, mixed — `mutedClips` holds the CLIP ids
   *  the user has silenced. Keyed by clip (not track) id so a mute survives lane re-packing,
   *  which mints new track ids (bug H1). Default empty = all sources audible. */
  mutedClips = $state<Set<string>>(new Set());
  isClipMuted(clipId: string): boolean {
    return this.mutedClips.has(clipId);
  }
  setClipsMuted(clipIds: string[], muted: boolean): void {
    const s = new Set(this.mutedClips);
    for (const id of clipIds) if (muted) s.add(id); else s.delete(id);
    this.mutedClips = s;
  }

  // ---- Undo for the timeline composition (Cmd/Ctrl-Z) --------------------------
  // Each discrete edit snapshots videoTracks + mutedAudio first; undo restores the
  // last snapshot. Clip URLs are NOT revoked on remove (they'd be dead on restore) — we
  // track them all and revoke in bulk on clearVideoTracks()/reset().
  private undoStack: { tracks: VideoTrack[]; muted: string[]; extra: unknown }[] = [];
  private objectUrls = new Set<string>();
  private rememberUrl(url: string): void {
    this.objectUrls.add(url);
  }
  // Component-local per-clip state (clipAnchor, syncUncertain) that isn't in videoTracks but
  // MUST be part of an undo step (bug H3). Player registers capture/apply hooks so a snapshot
  // carries it and undo restores it in lockstep with the tracks.
  undoCapture: (() => unknown) | null = null;
  undoApply: ((extra: unknown) => void) | null = null;
  /** Snapshot the current composition before a discrete edit. Call once per user gesture
   *  (drag start, one nudge, one add/remove) — NOT inside setClipOffset (fires per frame). */
  pushUndo(): void {
    this.undoStack.push({
      tracks: this.videoTracks.map((t) => ({ ...t, clips: t.clips.map((c) => ({ ...c })) })),
      muted: [...this.mutedClips],
      extra: this.undoCapture?.() ?? null,
    });
    if (this.undoStack.length > 80) this.undoStack.shift();
  }
  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  undo(): void {
    const snap = this.undoStack.pop();
    if (!snap) return;
    this.videoTracks = snap.tracks;
    this.mutedClips = new Set(snap.muted);
    this.undoApply?.(snap.extra);
    // Re-show any surfaces that a restored track needs (removed ones were hidden).
    for (const t of this.videoTracks) this.showSource((t.graded ? 'video' : `video:${t.id}`) as SourceId);
  }
  srtName = $state('');
  warnings = $state<string[]>([]);
  error = $state<string | null>(null);

  currentMs = $state(0);
  /** Time the user is hovering on a chart (ms); null when not hovering. Drives cross-highlight. */
  hoverMs = $state<number | null>(null);
  playing = $state(false);
  unitSystem = $state<UnitSystem>('metric');

  mainView = $state<MainView>('video');
  /** Dockable-tiling stage layout (video/map/… panes + floating PiPs). Supersedes
   *  mainView/split; mainView is kept only to seed this on first load. */
  stage = $state<StageLayout>({ tree: null, floats: [] });
  /** True once the user has explicitly arranged the stage (drag/hide/float/resize);
   *  until then the layout is derived from the available sources. */
  stageCustomized = $state(false);
  /** A surface shown fullscreen over the stage (all others hidden). Transient view
   *  state — deliberately NOT persisted; a reload returns to the normal layout. */
  fullscreenSource = $state<SourceId | null>(null);
  toggleFullscreen(s: SourceId): void {
    this.fullscreenSource = this.fullscreenSource === s ? null : s;
  }
  /** Split view: left pane width fraction, and whether video is the left pane. */
  splitFrac = $state(0.5);
  splitVideoLeft = $state(true);
  /** Picture-in-picture geometry as fractions of the stage. */
  pipX = $state(0.78);
  pipY = $state(0.72);
  pipW = $state(0.2);
  videoEl = $state<HTMLVideoElement | null>(null);
  /** Registered by the FlightMap pane while mounted; null when no map is open. */
  mapApi = $state<MapApi | null>(null);
  pathColorBy = $state<PathColorBy>('speed');
  autoFollow = $state(false);
  /** Show important-point (event) pins on the timeline scrubber. */
  timelineEvents = $state(true);
  /** 3D terrain on the map (DEM relief + camera pitch). */
  map3d = $state(false);
  /** Basemap layer. */
  basemap = $state<'streets' | 'satellite'>('streets');
  /** Configurable flight-path overlay options (markers, path style, buildings). */
  mapOpts = $state<MapOpts>({ ...DEFAULT_MAP_OPTS });

  /** Temporarily hide every on-stage widget (quick declutter). Not persisted —
   *  it's a momentary toggle, so a reload always brings the widgets back. */
  widgetsHidden = $state(false);

  dockFrac = $state(0.42);
  dockCollapsed = $state(false);
  /** Auto-apply the matching colour LUT to D-Log (flat) footage. */
  lutEnabled = $state(true);
  /** Manual colour grade on the video (composites over the LUT, burns into export). */
  colorAdjust = $state<ColorAdjust>({ ...DEFAULT_COLOR_ADJUST });
  /** DJI developer apiKey — SESSION-ONLY input buffer. The key is NOT persisted
   *  in the browser; it's stored server-side in a gitignored, owner-only file via
   *  /api/dji-key and read machine-side by the decoder. This just holds a
   *  just-typed value until it's saved. */
  djiApiKey = $state('');
  /** Whether the machine has a stored DJI key (from /api/dji-key). */
  djiKeySet = $state(false);
  /** Collapse the timeline's metric graph to a thin scrub strip. */
  timelineCollapsed = $state(false);
  /** Max height (px) of the scrollable timeline charts area — user-draggable via a
   *  splitter above the charts; charts scroll when they exceed it. */
  timelineHeight = $state(160);
  /** Filename prefixes whose videos auto-sync by their creation_time = recording END
   *  (subtract duration), not START. Verified against real footage (Desktop/temp,
   *  2026-07-08): RC2/DJI-app SCREEN recordings (`screen-…`) stamp the END — the file
   *  finalises when recording stops, and creation_time == the end wall-clock (confirmed
   *  by cross-referencing the "9500m" screen clip to the drone's 9500m moment in the
   *  SRT). Everything else — including DJI DRONE footage (`DJI_…`), Sony (`A001…`) and
   *  iPhone (`IMG_…`) — stamps the START (DJI creation_time == SRT frame 1 exactly), so
   *  it aligns by start and is NOT listed here. User-editable in Settings. Case-insensitive. */
  endAlignPrefixes = $state<string[]>(['screen-']);
  /** The default timecode anchor for a video filename, from `endAlignPrefixes`. */
  defaultAnchorFor(name: string): 'start' | 'end' {
    const n = name.toLowerCase();
    return this.endAlignPrefixes.some((p) => p && n.startsWith(p.toLowerCase())) ? 'end' : 'start';
  }
  widgets = $state<WidgetConfig[]>(defaultWidgets());

  /** Fullscreen viz modal (null = closed). */
  modal = $state<ModalViz | null>(null);
  openModal(title: string, comp: Component<Record<string, unknown>>, props: Record<string, unknown>): void {
    this.modal = { title, comp, props };
  }
  closeModal(): void {
    this.modal = null;
  }

  /** Widget type currently being dragged from the palette (enables the drop-catcher). */
  draggingPaletteType = $state<WidgetType | null>(null);

  /** Per-sample telemetry data table overlay open? */
  dataTableOpen = $state(false);

  /** Additional flights overlaid on the map for comparison (read-only paths). */
  compareFlights = $state<CompareFlight[]>([]);
  private compareSeq = 0;

  constructor() {
    // No network here. `app` is a module singleton, so anything the constructor fetches is fetched
    // by every importer — including builds with no dev server behind them, where it is a guaranteed
    // 404 on each load. The DJI key status is needed by exactly two screens; they ask for it.
    const saved = loadLayout();
    if (saved) {
      if (saved.unitSystem === 'metric' || saved.unitSystem === 'imperial') this.unitSystem = saved.unitSystem;
      if (saved.mainView === 'video' || saved.mainView === 'map' || saved.mainView === 'split') this.mainView = saved.mainView;
      if (saved.stageCustomized && saved.stage && typeof saved.stage === 'object' && 'tree' in saved.stage) {
        this.stage = { tree: saved.stage.tree ?? null, floats: Array.isArray(saved.stage.floats) ? saved.stage.floats : [] };
        this.stageCustomized = true;
        // One-time migration: the flights list became a dockable pane. Customised
        // stages saved before that predate it — inject 'flights' once so the pane
        // stays reachable (the user can still hide it afterwards; the flag sticks).
        if (!saved.flightsMigrated && this.stage.tree && !hasSource(this.stage.tree, 'flights')) {
          this.stage = addSource(this.stage, 'flights');
        }
      }
      if (
        saved.pathColorBy === 'none' ||
        saved.pathColorBy === 'speed' ||
        saved.pathColorBy === 'altitude' ||
        saved.pathColorBy === 'vspeed' ||
        saved.pathColorBy === 'dist' ||
        saved.pathColorBy === 'time'
      )
        this.pathColorBy = saved.pathColorBy;
      if (typeof saved.autoFollow === 'boolean') this.autoFollow = saved.autoFollow;
      if (typeof saved.timelineEvents === 'boolean') this.timelineEvents = saved.timelineEvents;
      if (typeof saved.map3d === 'boolean') this.map3d = saved.map3d;
      if (saved.basemap === 'streets' || saved.basemap === 'satellite') this.basemap = saved.basemap;
      if (saved.mapOpts && typeof saved.mapOpts === 'object') {
        const o = saved.mapOpts as Partial<MapOpts>;
        const m = this.mapOpts;
        if (typeof o.buildings === 'boolean') m.buildings = o.buildings;
        if (typeof o.showEvents === 'boolean') m.showEvents = o.showEvents;
        if (typeof o.showLoss === 'boolean') m.showLoss = o.showLoss;
        if (typeof o.showHover === 'boolean') m.showHover = o.showHover;
        if (Number.isFinite(o.pathWidth)) m.pathWidth = Math.max(1.5, Math.min(7, o.pathWidth as number));
        if (typeof o.pathGlow === 'boolean') m.pathGlow = o.pathGlow;
        if (typeof o.curtain3d === 'boolean') m.curtain3d = o.curtain3d;
        if (typeof o.showLos === 'boolean') m.showLos = o.showLos;
        if (typeof o.videoProjection === 'boolean') m.videoProjection = o.videoProjection;
        if (Number.isFinite(o.videoProjOpacity)) m.videoProjOpacity = Math.max(0.1, Math.min(1, o.videoProjOpacity as number));
      }
      if (typeof saved.splitFrac === 'number') this.splitFrac = Math.max(0.2, Math.min(0.8, saved.splitFrac));
      if (typeof saved.splitVideoLeft === 'boolean') this.splitVideoLeft = saved.splitVideoLeft;
      if (typeof saved.pipX === 'number') this.pipX = Math.max(0, Math.min(0.9, saved.pipX));
      if (typeof saved.pipY === 'number') this.pipY = Math.max(0, Math.min(0.9, saved.pipY));
      if (typeof saved.pipW === 'number') this.pipW = Math.max(0.1, Math.min(0.5, saved.pipW));
      if (typeof saved.dockFrac === 'number') this.dockFrac = Math.max(0.2, Math.min(0.7, saved.dockFrac));
      if (typeof saved.dockCollapsed === 'boolean') this.dockCollapsed = saved.dockCollapsed;
      if (typeof saved.lutEnabled === 'boolean') this.lutEnabled = saved.lutEnabled;
      if (saved.colorAdjust && typeof saved.colorAdjust === 'object') {
        // Validate each field numerically (mirrors the mapOpts guards above) — a corrupt/old
        // blob with a non-numeric field would otherwise flow straight into the CSS/SVG filter
        // and burn into the export (round-2 colorAdjust unguarded read).
        const c = saved.colorAdjust as Partial<ColorAdjust>;
        const next = { ...DEFAULT_COLOR_ADJUST };
        for (const k of Object.keys(next) as (keyof ColorAdjust)[]) {
          if (Number.isFinite(c[k])) next[k] = Math.max(-100, Math.min(100, c[k] as number));
        }
        this.colorAdjust = next;
      }
      if (typeof saved.timelineCollapsed === 'boolean') this.timelineCollapsed = saved.timelineCollapsed;
      if (Number.isFinite(saved.timelineHeight)) this.timelineHeight = Math.max(0, Math.min(600, saved.timelineHeight!));
      if (Array.isArray(saved.endAlignPrefixes)) {
        const list = saved.endAlignPrefixes.filter((p): p is string => typeof p === 'string');
        // One-time migration: the first shipped default (['DJI_','screen-recording-',
        // 'screen_recording','AV_']) was wrong — DJI drone footage is START-aligned, and
        // those screen prefixes didn't match real `screen-…` files. If the saved list is
        // still that exact buggy default, replace it with the corrected one.
        const buggy = ['DJI_', 'screen-recording-', 'screen_recording', 'AV_'];
        if (list.length === buggy.length && list.every((p, i) => p === buggy[i])) {
          // keep the field's corrected default (['screen-'])
        } else {
          this.endAlignPrefixes = list;
        }
      }
      if (saved.widgets) {
        for (const w of this.widgets) {
          const s = saved.widgets[w.type];
          if (!s) continue;
          w.enabled = !!s.enabled;
          if (Number.isFinite(s.x)) w.x = s.x;
          if (Number.isFinite(s.y)) w.y = s.y;
          if (s.ax === 'l' || s.ax === 'r') w.ax = s.ax;
          if (s.ay === 't' || s.ay === 'b') w.ay = s.ay;
          if (Number.isFinite(s.ox)) w.ox = s.ox;
          if (Number.isFinite(s.oy)) w.oy = s.oy;
          if (Number.isFinite(s.width)) w.width = Math.max(80, s.width as number);
          if (Number.isFinite(s.height)) w.height = Math.max(60, s.height as number);
          if (Number.isFinite(s.scale)) w.scale = Math.max(0.6, Math.min(2.2, s.scale));
          if (typeof s.color === 'string') w.color = s.color;
          if (s.bg === 'glass' || s.bg === 'none' || s.bg === 'card') w.bg = s.bg;
          if (typeof s.docked === 'boolean') w.docked = s.docked;
        }
      }
      if (Array.isArray(saved.userPresets)) {
        this.presets = [...this.presets, ...saved.userPresets.filter((p) => p && !p.builtin && p.layout)];
        const maxU = saved.userPresets.reduce((m, p) => Math.max(m, /^u(\d+)$/.test(p.id) ? +p.id.slice(1) : 0), 0);
        this.presetSeq = maxU + 1;
      }
      if (typeof saved.activePresetId === 'string') this.activePresetId = saved.activePresetId;
      if (saved.lastLayout && saved.lastLayout.layout) this.lastLayout = saved.lastLayout;
    }
  }

  // Absolute-time master timeline: t0 = the earliest source. Clip offsets are stored
  // relative to the flight start, so a clip that begins BEFORE the flight has a
  // negative offset; the whole timeline (flight + all clips) shifts right by this so
  // the earliest source sits at 0. Without any early-starting clip this is 0 (no
  // change from the flight-anchored model).
  baseOffsetMs = $derived.by(() => {
    let min = 0;
    for (const tr of this.videoTracks) for (const c of tr.clips) if (c.offsetMs < min) min = c.offsetMs;
    return -min;
  });
  /** Time within the flight (currentMs is master-timeline time; the flight sits at
   *  baseOffsetMs on it). Negative or past the flight duration = flight not active. */
  flightMs = $derived(this.currentMs - this.baseOffsetMs);
  // The sample INDEX changes only at sample boundaries (~10 Hz), while `currentMs`
  // ticks every animation frame (~60 Hz). Deriving the index first means `sample`
  // (and every widget that reads it) only recomputes when the index actually
  // changes — Svelte doesn't propagate an unchanged primitive downstream. This
  // removes ~5 of every 6 per-frame widget recomputations during playback.
  sampleIndex = $derived(this.flight ? indexAtTime(this.flight, this.flightMs) : -1);
  sample = $derived.by<SampleView | null>(() =>
    this.flight && this.sampleIndex >= 0 ? viewAt(this.flight, this.sampleIndex) : null,
  );

  events = $derived.by<FlightEvent[]>(() => (this.flight ? detectEvents(this.flight) : []));

  available = $derived.by<Set<string>>(() => {
    const s = new Set<string>();
    if (this.flight) for (const c of this.flight.channels) s.add(c.key);
    return s;
  });

  hasChannel(key: string): boolean {
    return this.available.has(key);
  }
  hasWidgetData(type: WidgetType): boolean {
    const meta = WIDGET_REGISTRY.find((m) => m.type === type);
    return !!meta && meta.requires.every((k) => this.available.has(k));
  }
  isRenderable(w: WidgetConfig): boolean {
    return w.enabled && this.hasWidgetData(w.type);
  }
  isAdded(type: WidgetType): boolean {
    return !!this.widgets.find((w) => w.type === type)?.enabled;
  }

  /** Enable a widget and place it at a fractional position (from a palette drop). */
  addWidget(type: WidgetType, x: number, y: number): void {
    const w = this.widgets.find((x2) => x2.type === type);
    if (!w) return;
    w.enabled = true;
    w.x = Math.max(0, Math.min(0.97, x));
    w.y = Math.max(0, Math.min(0.97, y));
    // Clear the anchor so the widget re-anchors to this fresh drop point on the
    // next layout (WidgetLayer computes ax/ay/ox/oy from x/y when ax is unset).
    w.ax = w.ay = undefined;
    w.ox = w.oy = undefined;
    this.persist();
  }
  removeWidget(type: WidgetType): void {
    const w = this.widgets.find((x) => x.type === type);
    if (w) {
      w.enabled = false;
      w.docked = false; // drop any grid slot too (availSources → pruned out)
    }
    this.persist();
  }
  isWidgetDocked(type: WidgetType): boolean {
    return !!this.widgets.find((w) => w.type === type)?.docked;
  }
  // ---- DJI key (stored server-side, never in the browser) ------------------
  private djiKeyStatusPromise: Promise<void> | null = null;
  /**
   * Load the stored-key status at most once, on demand. Callers are the screens that show or use it;
   * everyone else never triggers the request. Safe to call repeatedly.
   */
  ensureDjiKeyStatus(): Promise<void> {
    this.djiKeyStatusPromise ??= this.loadDjiKeyStatus();
    return this.djiKeyStatusPromise;
  }
  /** Is a key stored on this machine? Loads the status first if nobody has asked yet. */
  async hasStoredDjiKey(): Promise<boolean> {
    await this.ensureDjiKeyStatus();
    return this.djiKeySet;
  }
  async loadDjiKeyStatus(): Promise<void> {
    try {
      const r = await fetch('/api/dji-key');
      if (r.ok) this.djiKeySet = !!(await r.json()).set;
    } catch {
      /* decoder not running (static build) — leave as unset */
    }
  }
  /** Persist a just-typed key to the machine-local store, then clear the input. */
  async saveDjiKey(): Promise<boolean> {
    const key = this.djiApiKey.trim();
    if (!key) return false;
    try {
      const r = await fetch('/api/dji-key', { method: 'POST', body: key });
      if (!r.ok) return false;
      this.djiKeySet = true;
      this.djiApiKey = ''; // don't keep it around in the browser
      return true;
    } catch {
      return false;
    }
  }
  async clearDjiKey(): Promise<void> {
    this.djiApiKey = '';
    try {
      await fetch('/api/dji-key', { method: 'DELETE' });
    } catch {
      /* ignore */
    }
    this.djiKeySet = false;
  }
  /** Move a widget from its floating HUD overlay into a docked grid pane. */
  dockWidget(type: WidgetType): void {
    const w = this.widgets.find((x) => x.type === type);
    if (!w || w.docked) return;
    w.docked = true; // makes `w:<type>` available before we add it to the tree
    this.commitStage(addSource(this.effStage, `w:${type}`));
  }
  /** Return a docked widget to its floating HUD overlay. */
  undockWidget(type: WidgetType): void {
    const w = this.widgets.find((x) => x.type === type);
    if (!w || !w.docked) return;
    this.commitStage({
      tree: removeSource(this.effStage.tree, `w:${type}`),
      floats: this.effStage.floats.filter((f) => f.source !== `w:${type}`),
    });
    w.docked = false;
    this.persist();
  }
  setWidgetBg(type: WidgetType, bg: WidgetBg): void {
    const w = this.widgets.find((x) => x.type === type);
    if (w) w.bg = bg === 'card' ? undefined : bg;
    this.persist();
  }
  setWidgetColor(type: WidgetType, color: string | undefined): void {
    const w = this.widgets.find((x) => x.type === type);
    if (w) w.color = color;
    this.persist();
  }
  /** Generic per-widget update — common settings (opacity, titleHidden) and, via
   *  `opts`, widget-specific ones. Persists. */
  patchWidget(type: WidgetType, patch: Partial<WidgetConfig>): void {
    const w = this.widgets.find((x) => x.type === type);
    if (w) Object.assign(w, patch);
    this.persist();
  }
  /** Merge a single widget-specific option into `opts`. */
  setWidgetOpt(type: WidgetType, key: string, value: number | string | boolean): void {
    const w = this.widgets.find((x) => x.type === type);
    if (w) w.opts = { ...w.opts, [key]: value };
    this.persist();
  }
  /** Copy one time-series chart's display options to every other chart — so you
   *  tune one and the whole dashboard matches (smoothing, fill, line width, …). */
  applyChartOptsToAll(fromType: WidgetType): void {
    const src = this.widgets.find((x) => x.type === fromType);
    if (!src) return;
    for (const w of this.widgets) if (w.type.startsWith('ts_')) w.opts = { ...src.opts };
    this.persist();
  }
  /** Reset a widget's appearance settings (colour, bg, opacity, title, per-type opts)
   *  to defaults. Leaves layout (position/size) untouched. */
  resetWidgetSettings(type: WidgetType): void {
    const w = this.widgets.find((x) => x.type === type);
    if (w) {
      w.color = undefined;
      w.bg = undefined;
      w.opacity = undefined;
      w.titleHidden = undefined;
      w.opts = undefined;
    }
    this.persist();
  }

  // ---- Layout presets ------------------------------------------------------
  presets = $state<LayoutPreset[]>(BUILTIN_PRESETS.map((p) => ({ ...p, layout: { ...p.layout } })));
  activePresetId = $state('overview');
  /** The working layout auto-saved just before a switch, so an accidental preset
   *  change never loses hand-tuned edits — one click restores it. */
  lastLayout = $state<LayoutPreset | null>(null);
  private presetSeq = 1;

  /** Snapshot of the currently-placed widgets (enabled ones + geometry). */
  currentLayout(): Record<string, PresetPlacement> {
    const out: Record<string, PresetPlacement> = {};
    for (const w of this.widgets) if (w.enabled) out[w.type] = { x: w.x, y: w.y, scale: w.scale, color: w.color };
    return out;
  }
  /** True when the live layout differs from the active preset (shows "• edited"). */
  layoutDirty = $derived.by(() => {
    const p = this.presets.find((x) => x.id === this.activePresetId);
    return p ? !layoutsEqual(this.currentLayout(), p.layout) : true;
  });

  private applyLayout(layout: Record<string, PresetPlacement>): void {
    for (const w of this.widgets) {
      const l = layout[w.type];
      if (l) {
        w.enabled = true;
        w.x = l.x;
        w.y = l.y;
        w.scale = l.scale ?? 1;
        w.color = l.color;
      } else {
        w.enabled = false;
      }
    }
  }
  applyPreset(id: string): void {
    const p = this.presets.find((x) => x.id === id);
    if (!p) return;
    // preserve hand-tuned edits before switching away
    if (this.layoutDirty) this.lastLayout = { id: 'last', name: '↩ Previous layout', builtin: false, layout: this.currentLayout() };
    this.applyLayout(p.layout);
    this.activePresetId = id;
    this.persist();
  }
  saveAsPreset(name: string): string {
    const id = `u${this.presetSeq++}`;
    this.presets = [...this.presets, { id, name: name.trim() || 'My layout', builtin: false, layout: this.currentLayout() }];
    this.activePresetId = id;
    this.persist();
    return id;
  }
  updatePreset(id: string): void {
    const p = this.presets.find((x) => x.id === id);
    if (p && !p.builtin) {
      p.layout = this.currentLayout();
      this.persist();
    }
  }
  deletePreset(id: string): void {
    this.presets = this.presets.filter((p) => p.id !== id || p.builtin);
    if (this.activePresetId === id) this.activePresetId = 'overview';
    this.persist();
  }
  restoreLast(): void {
    if (!this.lastLayout) return;
    this.applyLayout(this.lastLayout.layout);
    this.activePresetId = 'custom'; // doesn't match a preset → shows as edited
    this.lastLayout = null;
    this.persist();
  }

  persist(): void {
    const widgets: Record<string, SavedWidget> = {};
    for (const w of this.widgets) widgets[w.type] = { enabled: w.enabled, x: w.x, y: w.y, ax: w.ax, ay: w.ay, ox: w.ox, oy: w.oy, scale: w.scale, width: w.width, height: w.height, color: w.color, bg: w.bg, docked: w.docked };
    try {
      const payload: SavedLayout = {
        unitSystem: this.unitSystem,
        dockFrac: this.dockFrac,
        dockCollapsed: this.dockCollapsed,
        lutEnabled: this.lutEnabled,
        colorAdjust: this.colorAdjust,
        timelineCollapsed: this.timelineCollapsed,
        timelineHeight: this.timelineHeight,
        endAlignPrefixes: this.endAlignPrefixes,
        mainView: this.mainView,
        splitFrac: this.splitFrac,
        splitVideoLeft: this.splitVideoLeft,
        pipX: this.pipX,
        pipY: this.pipY,
        pipW: this.pipW,
        pathColorBy: this.pathColorBy,
        autoFollow: this.autoFollow,
        timelineEvents: this.timelineEvents,
        map3d: this.map3d,
        basemap: this.basemap,
        mapOpts: this.mapOpts,
        stage: this.stage,
        stageCustomized: this.stageCustomized,
        flightsMigrated: true,
        widgets,
        userPresets: this.presets.filter((p) => !p.builtin),
        activePresetId: this.activePresetId,
        lastLayout: this.lastLayout,
      };
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } catch {
      // storage unavailable; layout won't persist
    }
  }

  resetLayout(): void {
    this.widgets = defaultWidgets();
    this.persist();
  }

  // ---- Dockable stage --------------------------------------------------------
  // Until the user arranges the stage, the effective layout is *derived* from the
  // available sources (so a source loading late — e.g. the video after the SRT —
  // appears automatically). The first explicit edit materialises that layout into
  // `stage` and flips `stageCustomized`; from then on it's authoritative.
  /** Sources that can appear on the stage: video/map when present, plus the
   *  always-available catalog (widget palette) and data (telemetry table). */
  // The flights list is always an available pane (it's how you pick a flight, even
  // before one is loaded). catalog/data need a loaded flight to be meaningful.
  availSources = $derived(
    new Set<SourceId>([
      'flights',
      // Flight planning is a standalone map (SHGM zones + weather) — no loaded flight required.
      'planning',
      ...(this.videoUrl ? ['video'] : []),
      ...(this.hasChannel('latitude') ? ['map'] : []),
      ...(this.flight ? ['catalog', 'data'] : []),
      // Library-wide views — dockable panes, shown via the bottom-bar buttons.
      ...(this.flight ? ['workspace', 'allflights'] : []),
      // Widgets docked into the grid become panes with a `w:<type>` source id. Gate
      // on isRenderable (flight + required channels present), like the overlay path —
      // otherwise a docked widget from a persisted layout renders with no flight
      // loaded (flight isn't persisted) and crashes reading flight.channels.
      ...this.widgets.filter((w) => w.docked && this.isRenderable(w)).map((w) => `w:${w.type}` as SourceId),
      // Settings panels are surfaces too (`set:<panel>`): opened as a float from a
      // bar button, then dockable / fullscreenable through the one shared pane infra.
      ...(this.videoUrl ? ['set:video' as SourceId] : []),
      ...(this.hasChannel('latitude') ? ['set:map' as SourceId] : []),
      // Extra synced video tracks render as `video:<trackId>` surfaces (dock/float/full).
      // The graded hero is excluded — it renders in the 'video' pane, not a `video:<id>` one.
      ...this.videoTracks.filter((t) => !t.graded).map((t) => `video:${t.id}` as SourceId),
      'set:app' as SourceId, // general settings — always reachable from the ⚙ button
    ]),
  );
  /** The default (uncustomised) layout: flights on the left, and the media
   *  (video/map on top, catalog below) filling the rest. With no flight loaded the
   *  flights pane fills the whole stage — that's the empty state. */
  defaultStage(): StageLayout {
    const media = fromModes(this.mainView, this.splitFrac, this.splitVideoLeft, !!this.videoUrl, this.hasChannel('latitude'));
    const right: TileNode | null = media.tree
      ? { kind: 'split', dir: 'col', sizes: [0.6, 0.4], children: [media.tree, leaf('catalog')] }
      : this.flight
        ? leaf('catalog')
        : null;
    if (!right) return { tree: leaf('flights'), floats: [] };
    const tree: TileNode = { kind: 'split', dir: 'row', sizes: [0.2, 0.8], children: [leaf('flights'), right] };
    return { tree, floats: media.floats };
  }
  /** Effective layout: derive from sources until the user arranges the stage; then
   *  use the saved tree. Always pruned to available sources and never empty. */
  effStage = $derived.by<StageLayout>(() => {
    const base = this.stageCustomized ? this.stage : this.defaultStage();
    const tree = pruneTo(base.tree, this.availSources);
    const floats = base.floats.filter((f) => this.availSources.has(f.source));
    // An empty stage is only safe WHILE a flight is loaded — the transport bar's
    // pane chips bring panes back (this is the "close the last pane without it
    // springing open again" behaviour). With NO flight there is no transport bar, so
    // an empty stage would be a dead black screen (the empty-state hero lives inside
    // the flights pane); seed the entry pane instead so recovery is always possible.
    if (!tree && floats.length === 0 && !(this.flight && this.stageCustomized)) {
      const first = [...this.availSources][0];
      return { tree: first ? leaf(first) : null, floats };
    }
    return { tree, floats };
  });
  isSourceVisible(s: SourceId): boolean {
    return treeSources(this.effStage.tree).includes(s) || this.effStage.floats.some((f) => f.source === s);
  }
  /** Commit an explicit arrangement (persisted). */
  commitStage(layout: StageLayout): void {
    this.stage = layout;
    this.stageCustomized = true;
    this.persist();
  }
  /** Live update during a drag (persist deferred to the caller's pointerup). */
  setStageLive(layout: StageLayout): void {
    this.stage = layout;
    this.stageCustomized = true;
  }
  /** Where each hidden source last sat, so reopening returns it to its old spot
   *  instead of always docking to the right. In-memory (per session). */
  private hideHints = new Map<SourceId, DockHint>();
  showSource(s: SourceId): void {
    if (this.effStage.floats.some((f) => f.source === s)) return;
    // Restore to the remembered neighbour/side when that neighbour is still up.
    const hint = this.hideHints.get(s);
    this.hideHints.delete(s);
    if (hint && hasSource(this.effStage.tree, hint.neighbor)) {
      this.commitStage(dockOnto(this.effStage, s, hint.neighbor, hint.side));
      return;
    }
    this.commitStage(addSource(this.effStage, s));
  }
  hideSource(s: SourceId): void {
    const hint = dockHint(this.effStage.tree, s);
    if (hint) this.hideHints.set(s, hint);
    else this.hideHints.delete(s);
    this.commitStage({ tree: removeSource(this.effStage.tree, s), floats: this.effStage.floats.filter((f) => f.source !== s) });
  }
  toggleSource(s: SourceId): void {
    this.isSourceVisible(s) ? this.hideSource(s) : this.showSource(s);
  }
  /** Pop a tiled pane out to a floating PiP, remembering its tiled spot so docking
   *  it back restores that position. New floats are staggered so they don't stack. */
  floatSource(s: SourceId): void {
    if (this.effStage.floats.some((f) => f.source === s)) return; // already a float
    const hint = dockHint(this.effStage.tree, s);
    if (hint) this.hideHints.set(s, hint);
    const n = this.effStage.floats.length;
    const x = Math.max(0.04, 0.72 - n * 0.05);
    const y = Math.max(0.04, 0.68 - n * 0.05);
    // w≈h fraction ⇒ ~16:9 on a 16:9 stage; `h` set so the float is free-resizable
    // (per-axis) from the start rather than aspect-locked.
    this.commitStage({ tree: removeSource(this.effStage.tree, s), floats: [...this.effStage.floats, { source: s, x, y, w: 0.26, h: 0.26 }] });
  }
  /** Open a source as a float at a specific stage-fraction position/size — used to
   *  spawn settings panels near their bar button (item 3). No-op if already shown. */
  floatSourceAt(s: SourceId, x: number, y: number, w: number, h: number): void {
    if (this.isSourceVisible(s)) return;
    this.commitStage({ tree: removeSource(this.effStage.tree, s), floats: [...this.effStage.floats, { source: s, x, y, w, h }] });
  }
  dockSource(s: SourceId): void {
    const floats = this.effStage.floats.filter((f) => f.source !== s);
    // Restore to the remembered neighbour/side (from when it was popped out).
    const hint = this.hideHints.get(s);
    this.hideHints.delete(s);
    if (hint && hasSource(this.effStage.tree, hint.neighbor)) {
      this.commitStage(dockOnto({ tree: this.effStage.tree, floats }, s, hint.neighbor, hint.side));
      return;
    }
    this.commitStage(addSource({ tree: this.effStage.tree, floats }, s));
  }

  addCompare(flight: Flight, name: string, srcKey?: string): void {
    // Dedupe by source key when known (names collide on DJI defaults), else by name.
    const dup = srcKey
      ? this.compareFlights.some((c) => c.srcKey === srcKey)
      : this.compareFlights.some((c) => c.name === name);
    if (dup) return;
    const color = COMPARE_COLORS[this.compareSeq % COMPARE_COLORS.length];
    // Keep only the path channels; the rest of `flight` is free to be collected.
    const num = flight.samples.numeric;
    const path: ComparePath = {
      lat: num['latitude'],
      lon: num['longitude'],
      enuE: num['enu_e'],
      enuN: num['enu_n'],
      length: flight.samples.length,
    };
    this.compareFlights.push({ id: `cmp-${this.compareSeq++}`, srcKey, name, path, color });
  }
  removeCompare(id: string): void {
    this.compareFlights = this.compareFlights.filter((c) => c.id !== id);
  }
  clearCompare(): void {
    this.compareFlights = [];
  }

  /** True while a DJI flight log is decoding (drives a loading indicator). */
  logDecoding = $state(false);
  /** Set an already-built flight (e.g. from a decoded DJI log). */
  /** §2.4 per-channel source comparisons for the active flight (empty unless it was merged). */
  comparisons = $state<import('./import/mergeFlights.ts').ChannelComparison[]>([]);
  loadFlight(flight: Flight, warnings: string[], name: string): void {
    this.flight = flight;
    this.srtName = name;
    this.warnings = warnings;
    this.error = null;
    this.currentMs = 0;
    this.comparisons = [];
  }
  /** Load a flight merged from multiple sources (§2.1), keeping its verify comparisons (§2.4). */
  loadMerged(flight: Flight, warnings: string[], name: string, comparisons: import('./import/mergeFlights.ts').ChannelComparison[]): void {
    this.loadFlight(flight, warnings, name);
    this.comparisons = comparisons;
  }
  loadSrt(text: string, name: string): void {
    try {
      const { flight, warnings } = loadFlightFromSrt(text, name);
      this.flight = flight;
      this.srtName = name;
      this.warnings = warnings;
      this.error = null;
      this.currentMs = 0;
      // Overlays are deliberate cross-flight comparisons (set from the workspace or
      // sidebar) — they persist across active-flight switches; the user clears them.
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  /** Set the graded "hero" video (the folded former primary). Creates the graded track
   *  if none exists, else replaces its clip(s) with this single video — WITHOUT touching
   *  the other tracks. The graded track leads the lane order. Returns {trackId, clipId}. */
  setGradedVideo(url: string, name: string): { trackId: string; clipId: string } {
    this.rememberUrl(url);
    const clipId = `c${++this.clipSeq}`;
    const existing = this.videoTracks.find((t) => t.graded);
    if (existing) {
      // NB: don't revoke the old clip URL — undo may need to restore it (bulk-revoked later).
      this.videoTracks = this.videoTracks.map((t) =>
        t.id === existing.id ? { ...t, name, clips: [{ id: clipId, url, name, offsetMs: 0 }] } : t,
      );
      this.showSource('video'); // the graded hero renders in the 'video' pane
      return { trackId: existing.id, clipId };
    }
    const trackId = `t${++this.trackSeq}`;
    this.videoTracks = [{ id: trackId, name, graded: true, clips: [{ id: clipId, url, name, offsetMs: 0 }] }, ...this.videoTracks];
    this.showSource('video');
    return { trackId, clipId };
  }
  /** New video track (one clip) shown docked in the grid. Returns {trackId, clipId}. */
  addVideoTrack(url: string, name: string): { trackId: string; clipId: string } {
    this.rememberUrl(url);
    const trackId = `t${++this.trackSeq}`;
    const clipId = `c${++this.clipSeq}`;
    this.videoTracks = [...this.videoTracks, { id: trackId, name, clips: [{ id: clipId, url, name, offsetMs: 0 }] }];
    this.showSource(`video:${trackId}` as SourceId); // place it in the layout (else invisible)
    return { trackId, clipId };
  }
  /** Replace the NON-graded tracks with a packed arrangement — each inner array is one
   *  lane's clips (in order). Keeps the graded hero untouched. Used after a bulk add to
   *  collapse non-overlapping clips into the fewest lanes. */
  setNonGradedLanes(lanes: { id: string; url: string; name: string; offsetMs: number }[][]): void {
    for (const t of this.videoTracks) if (!t.graded) this.hideSource(`video:${t.id}` as SourceId);
    const graded = this.videoTracks.filter((t) => t.graded);
    const packed = lanes
      .filter((clips) => clips.length)
      .map((clips) => ({ id: `t${++this.trackSeq}`, name: clips[0].name, clips: clips.map((c) => ({ ...c })) }));
    this.videoTracks = [...graded, ...packed];
    for (const t of packed) this.showSource(`video:${t.id}` as SourceId);
  }
  /** Append a clip to an existing track (e.g. the next DJI split). Returns clipId. */
  addClipToTrack(trackId: string, url: string, name: string): string {
    this.rememberUrl(url);
    const clipId = `c${++this.clipSeq}`;
    this.videoTracks = this.videoTracks.map((t) =>
      t.id === trackId ? { ...t, clips: [...t.clips, { id: clipId, url, name, offsetMs: 0 }] } : t,
    );
    return clipId;
  }
  setClipOffset(clipId: string, offsetMs: number): void {
    this.videoTracks = this.videoTracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => (c.id === clipId ? { ...c, offsetMs } : c)),
    }));
  }
  /** Append an empty non-graded lane — a drop target for arranging clips into a new row.
   *  No stage pane yet (nothing to show); moveClipToTrack reveals the pane when a clip lands. */
  addEmptyLane(name = 'Lane'): string {
    const trackId = `t${++this.trackSeq}`;
    this.videoTracks = [...this.videoTracks, { id: trackId, name, clips: [] }];
    return trackId;
  }
  /** Move a clip to another track (drag across lanes, CapCut-style). Drops the source
   *  track + its surface if it becomes empty. Offset (timeline position) is kept. */
  moveClipToTrack(clipId: string, targetTrackId: string): void {
    const src = this.videoTracks.find((t) => t.clips.some((c) => c.id === clipId));
    const clip = src?.clips.find((c) => c.id === clipId);
    if (!src || !clip || src.id === targetTrackId) return;
    const emptied = src.clips.length === 1;
    const targetWasEmpty = this.videoTracks.find((t) => t.id === targetTrackId)?.clips.length === 0;
    this.videoTracks = this.videoTracks
      .map((t) =>
        t.id === src.id ? { ...t, clips: t.clips.filter((c) => c.id !== clipId) } : t.id === targetTrackId ? { ...t, clips: [...t.clips, clip] } : t,
      )
      // Drop ONLY the source lane if this move emptied it — keep any user-added empty lanes.
      .filter((t) => t.id !== src.id || t.clips.length > 0);
    if (targetWasEmpty) this.showSource(`video:${targetTrackId}` as SourceId); // reveal the new lane's pane
    if (emptied) this.hideSource(`video:${src.id}` as SourceId);
  }
  /** Remove one clip; drop the whole track (and its surface) if it becomes empty. */
  removeClip(clipId: string): void {
    // URLs are revoked in bulk on clear/reset — NOT here, so undo can restore a removal.
    const track = this.videoTracks.find((t) => t.clips.some((c) => c.id === clipId));
    if (track && track.clips.length === 1) { this.removeVideoTrack(track.id); return; }
    this.videoTracks = this.videoTracks.map((t) => ({ ...t, clips: t.clips.filter((c) => c.id !== clipId) }));
    this.pruneMutedClips();
  }
  removeVideoTrack(trackId: string): void {
    this.videoTracks = this.videoTracks.filter((x) => x.id !== trackId);
    this.pruneMutedClips();
    this.hideSource(`video:${trackId}` as SourceId);
  }
  /** Drop mute flags for clips that no longer exist (mute is keyed by clip id). */
  private pruneMutedClips(): void {
    const live = new Set(this.videoTracks.flatMap((t) => t.clips.map((c) => c.id)));
    if ([...this.mutedClips].every((id) => live.has(id))) return;
    this.mutedClips = new Set([...this.mutedClips].filter((id) => live.has(id)));
  }
  clearVideoTracks(): void {
    // A genuinely new project: now it's safe to free every URL and drop undo history.
    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls.clear();
    this.undoStack = [];
    this.videoTracks = [];
    this.mutedClips = new Set();
  }

  reset(): void {
    this.clearVideoTracks(); // revokes every clip URL (incl. the graded hero)
    this.flight = null;
    this.srtName = '';
    this.warnings = [];
    this.error = null;
    this.currentMs = 0;
    this.playing = false;
    // reset() means "no flight / empty library" — close any overlay that would
    // otherwise strand over a blank stage with frozen, now-flightless data.
    this.modal = null;
    this.dataTableOpen = false;
  }
}

export const app = new AppState();
